import type { CSSProperties } from "react";
import { Package } from "lucide-react";
import { showVatOn } from "@wa/core/sales-vat";
import { useT } from "../../../i18n/I18nProvider";
import { useInvoicingVat } from "../../useInvoicingVat";
import { formatCents, type LineItem, type QtyMode } from "../../../lib/sales";
import { EmptyState, listCard } from "../../ui";
import { qtyModeHead } from "./shared";

/**
 * The read-only twin of `shared/LineItemsEditor`: the same seven tracks, minus
 * the editor's delete column. Reading a document and editing it have to put the
 * same figure under the same heading, or pressing Edit means finding every
 * column again.
 *
 * This stays a grid of cells rather than a row of pills for the reason set out
 * in the editor: a pill row is a list of objects you click on, and a document's
 * lines are a matrix of numbers you read down a column and compare. Six figures
 * folded into a pill cannot be compared at all.
 */
const COLS = "minmax(130px,2.4fr) 72px 82px 104px 66px 70px 100px";

/** The same row without the VAT track, for a document that charges none. Mirrors
 *  `COLS_NO_VAT` in the editor, for the reason set out there. */
const COLS_NO_VAT = "minmax(130px,2.4fr) 72px 82px 104px 70px 100px";

/** Below this the seven tracks stop fitting, so the card scrolls sideways
 *  instead of squeezing the price column into three characters. The 20px is the
 *  row's own inset from the card edge - the tracks live inside it. */
const MIN_W = 762 + 20;
/** One track narrower, so a no-VAT table starts scrolling that much later. */
const MIN_W_NO_VAT = MIN_W - 66;

/**
 * What one line comes to: gross, less the discount, plus VAT on what is left.
 * Written the same way round as the editor's preview so the read view and the
 * edit view can never disagree by a cent.
 */
function lineTotalCents(li: LineItem): number {
  const gross = (li.qty || 0) * (li.unitPriceCents || 0);
  const discount = li.discountPct ? (gross * li.discountPct) / 100 : 0;
  const net = gross - discount;
  return Math.round(net + (net * (li.taxRatePct ?? 0)) / 100);
}

export function DocLines({ lines, qtyMode }: { lines: LineItem[]; qtyMode: QtyMode }) {
  const t = useT();
  const { vatEnabled } = useInvoicingVat();
  // Judged on the lines, so a document issued back when the company charged VAT
  // keeps showing what it charged. See `@wa/core/sales-vat`.
  const showVat = showVatOn({ vatEnabled, lines });
  const cols = showVat ? COLS : COLS_NO_VAT;
  const minW = showVat ? MIN_W : MIN_W_NO_VAT;

  // No minimum width on the empty state: there are no columns to hold apart
  // yet, and forcing one would hand an empty card a scrollbar.
  if (lines.length === 0) {
    return <EmptyState icon={Package} title={t("sales.line.none")} />;
  }

  return (
    // `crm-line-wrap` / `crm-line-grid` / `crm-line-row`: the same fold the
    // editor uses, so reading a document on a phone and editing it put the same
    // figure in the same place. `crm-line-ro` is the seven-cell variant - there
    // is no bin here, so the description takes the whole first line.
    <div className="crm-line-wrap overflow-x-auto">
      <div className="crm-line-grid" style={{ "--crm-line-min": `${minW}px` } as CSSProperties}>
        <div className="crm-list-head" style={{ ...listCard(cols), gridTemplateColumns: cols, paddingTop: 18 }}>
          <span>{t("sales.line.description")}</span>
          <span>{qtyModeHead(t, qtyMode)}</span>
          <span>{t("sales.line.unit")}</span>
          <span>{t("sales.line.unitPrice")}</span>
          {showVat ? <span>{t("sales.line.tax")}</span> : null}
          <span>{t("sales.line.discount")}</span>
          <span className="text-right">{t("sales.line.total")}</span>
        </div>

        <div className="flex flex-col gap-0.5 pb-2">
          {lines.map((li, idx) => (
            <div
              key={idx}
              className="crm-grid-row crm-grid-body crm-line-row crm-line-ro"
              style={{ "--crm-line-cols": cols } as CSSProperties}
            >
              <span className="crm-line-cell">
                <span className="crm-line-cap">{t("sales.line.description")}</span>
                <span className="crm-trunc block text-[12.75px] font-medium" title={li.description}>
                  {li.description}
                </span>
                {/* The detail paragraph, wrapped rather than truncated: it is
                    the half of the line a client actually reads, and one
                    elided line of it says nothing. */}
                {li.details ? <span className="crm-line-det">{li.details}</span> : null}
              </span>
              <span className="crm-line-cell">
                <span className="crm-line-cap">{qtyModeHead(t, qtyMode)}</span>
                <span className="crm-tnum block text-[12.75px]">{li.qty}</span>
              </span>
              <span className="crm-line-cell">
                <span className="crm-line-cap">{t("sales.line.unit")}</span>
                <span className="crm-mut crm-trunc block text-[12.75px]">{li.unit || "-"}</span>
              </span>
              <span className="crm-line-cell">
                <span className="crm-line-cap">{t("sales.line.unitPrice")}</span>
                <span className="crm-tnum block text-[12.75px]">{formatCents(li.unitPriceCents)}</span>
              </span>
              {showVat ? (
                <span className="crm-line-cell">
                  <span className="crm-line-cap">{t("sales.line.tax")}</span>
                  <span className="crm-tnum block text-[12.75px]">{li.taxRatePct}%</span>
                </span>
              ) : null}
              <span className="crm-line-cell">
                <span className="crm-line-cap">{t("sales.line.discount")}</span>
                <span className="crm-tnum block text-[12.75px]">{li.discountPct ? `${li.discountPct}%` : "-"}</span>
              </span>
              <span className="crm-line-cell crm-line-sum text-right">
                <span className="crm-line-cap">{t("sales.line.total")}</span>
                <span className="crm-tnum block text-[13.25px] font-bold">{formatCents(lineTotalCents(li))}</span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
